import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock, User, Mail, MessageSquare, Globe, CheckCircle, XCircle, AlertCircle, RefreshCw, Filter, Download, Search, BarChart3, TrendingUp, DollarSign } from 'lucide-react';
import { bookingService, Booking } from '../services/api';
import { t } from '../services/i18n';
import { BookingDetailsModal } from './BookingDetailsModal';

interface AdminConsultingDashboardProps {
  currentUser: any;
}

const AdminConsultingDashboard: React.FC<AdminConsultingDashboardProps> = ({ currentUser }) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    consultingType: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  useEffect(() => {
    loadBookings();
  }, [filters]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const result = await bookingService.getAllBookings(filters);
      setBookings(result.bookings);
    } catch (error: any) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (bookingId: string) => {
    if (!confirm(t('consulting.confirmCancel') || 'Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      await bookingService.cancelBooking(bookingId, 'Cancelled by admin');
      loadBookings();
    } catch (error: any) {
      console.error('Failed to cancel booking:', error);
      alert(error.message || 'Failed to cancel booking');
    }
  };

  const filteredBookings = bookings.filter(booking => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      booking.bookingId.toLowerCase().includes(term) ||
      booking.clientInfo.fullName.toLowerCase().includes(term) ||
      booking.clientInfo.email.toLowerCase().includes(term) ||
      booking.consultingType.toLowerCase().includes(term)
    );
  });

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; icon: any }> = {
      confirmed: { bg: 'bg-green-500/20', text: 'text-green-400', icon: CheckCircle },
      completed: { bg: 'bg-blue-500/20', text: 'text-blue-400', icon: CheckCircle },
      cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', icon: XCircle },
      'no-show': { bg: 'bg-yellow-500/20', text: 'text-yellow-400', icon: AlertCircle }
    };

    const badge = badges[status] || badges.confirmed;
    const Icon = badge.icon;

    return (
      <span className={`${badge.bg} ${badge.text} px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit`}>
        <Icon size={14} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getPaymentStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string }> = {
      completed: { bg: 'bg-green-500/20', text: 'text-green-400' },
      pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
      failed: { bg: 'bg-red-500/20', text: 'text-red-400' },
      refunded: { bg: 'bg-gray-500/20', text: 'text-gray-400' }
    };

    const badge = badges[status] || badges.pending;

    return (
      <span className={`${badge.bg} ${badge.text} px-2 py-1 rounded text-xs font-medium`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const handleViewBooking = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowBookingModal(true);
  };

  const exportToCSV = () => {
    const headers = [
      'Booking ID',
      'Date',
      'Time',
      'Client Name',
      'Email',
      'Telegram',
      'Phone',
      'Website',
      'Service Type',
      'Duration (min)',
      'Price (€)',
      'Payment Status',
      'Booking Status',
      'Topic',
      'Created At',
      'Updated At'
    ];

    const rows = filteredBookings.map(booking => [
      booking.bookingId,
      new Date(booking.date).toLocaleDateString(),
      `${booking.startTime} - ${booking.endTime}`,
      booking.clientInfo.fullName,
      booking.clientInfo.email,
      booking.clientInfo.telegram || '',
      booking.clientInfo.phone || '',
      booking.clientInfo.website || '',
      booking.consultingType.toUpperCase(),
      booking.duration.toString(),
      booking.price.toString(),
      booking.payment.status,
      booking.status,
      booking.consultationDetails.topic.replace(/,/g, ';'), // Replace commas to avoid CSV issues
      new Date(booking.createdAt).toISOString(),
      new Date(booking.updatedAt).toISOString()
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `bookings_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Analytics calculations
  const analytics = useMemo(() => {
    const byDay: Record<string, { count: number; revenue: number }> = {};
    const byMonth: Record<string, { count: number; revenue: number; byType: Record<string, { count: number; revenue: number }> }> = {};
    const byType: Record<string, { count: number; revenue: number; avgPrice: number }> = {
      seo: { count: 0, revenue: 0, avgPrice: 0 },
      wordpress: { count: 0, revenue: 0, avgPrice: 0 },
      woocommerce: { count: 0, revenue: 0, avgPrice: 0 }
    };

    bookings.forEach(booking => {
      if (booking.payment.status !== 'completed') return;

      const date = new Date(booking.date);
      const dayKey = date.toISOString().split('T')[0];
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const type = booking.consultingType;

      // By day
      if (!byDay[dayKey]) {
        byDay[dayKey] = { count: 0, revenue: 0 };
      }
      byDay[dayKey].count++;
      byDay[dayKey].revenue += booking.price;

      // By month
      if (!byMonth[monthKey]) {
        byMonth[monthKey] = { count: 0, revenue: 0, byType: {} };
      }
      byMonth[monthKey].count++;
      byMonth[monthKey].revenue += booking.price;
      if (!byMonth[monthKey].byType[type]) {
        byMonth[monthKey].byType[type] = { count: 0, revenue: 0 };
      }
      byMonth[monthKey].byType[type].count++;
      byMonth[monthKey].byType[type].revenue += booking.price;

      // By type
      byType[type].count++;
      byType[type].revenue += booking.price;
    });

    // Calculate averages
    Object.keys(byType).forEach(type => {
      if (byType[type].count > 0) {
        byType[type].avgPrice = byType[type].revenue / byType[type].count;
      }
    });

    return { byDay, byMonth, byType };
  }, [bookings]);

  const stats = {
    total: bookings.length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    completed: bookings.filter(b => b.status === 'completed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length,
    revenue: bookings
      .filter(b => b.payment.status === 'completed')
      .reduce((sum, b) => sum + b.price, 0)
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            {t('consulting.adminTitle') || 'Consulting Bookings Management'}
          </h1>
          <p className="text-slate-400">
            {t('consulting.adminSubtitle') || 'Manage all consulting bookings and sessions'}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="text-slate-400 text-sm mb-1">{t('consulting.statsTotal') || 'Total Bookings'}</div>
            <div className="text-3xl font-bold text-white">{stats.total}</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="text-slate-400 text-sm mb-1">{t('consulting.statsConfirmed') || 'Confirmed'}</div>
            <div className="text-3xl font-bold text-green-400">{stats.confirmed}</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="text-slate-400 text-sm mb-1">{t('consulting.statsCompleted') || 'Completed'}</div>
            <div className="text-3xl font-bold text-blue-400">{stats.completed}</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="text-slate-400 text-sm mb-1">{t('consulting.statsCancelled') || 'Cancelled'}</div>
            <div className="text-3xl font-bold text-red-400">{stats.cancelled}</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="text-slate-400 text-sm mb-1">{t('consulting.statsRevenue') || 'Total Revenue'}</div>
            <div className="text-3xl font-bold text-indigo-400">{stats.revenue} €</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-slate-300 text-sm mb-2">{t('consulting.filterStartDate') || 'Start Date'}</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full p-2 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-slate-300 text-sm mb-2">{t('consulting.filterEndDate') || 'End Date'}</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full p-2 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-slate-300 text-sm mb-2">{t('consulting.filterStatus') || 'Status'}</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="w-full p-2 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="">{t('consulting.all') || 'All'}</option>
                <option value="confirmed">{t('consulting.confirmed') || 'Confirmed'}</option>
                <option value="completed">{t('consulting.completed') || 'Completed'}</option>
                <option value="cancelled">{t('consulting.cancelled') || 'Cancelled'}</option>
                <option value="no-show">{t('consulting.noShow') || 'No Show'}</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-300 text-sm mb-2">{t('consulting.filterType') || 'Type'}</label>
              <select
                value={filters.consultingType}
                onChange={(e) => setFilters({ ...filters, consultingType: e.target.value })}
                className="w-full p-2 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
              >
                <option value="">{t('consulting.all') || 'All'}</option>
                <option value="seo">SEO</option>
                <option value="wordpress">WordPress</option>
                <option value="woocommerce">WooCommerce</option>
              </select>
            </div>
            <div>
              <label className="block text-slate-300 text-sm mb-2">{t('consulting.search') || 'Search'}</label>
              <div className="relative">
                <Search size={18} className="absolute left-3 top-2.5 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('consulting.searchPlaceholder') || 'Search bookings...'}
                  className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={exportToCSV}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-all flex items-center gap-2"
            >
              <Download size={18} />
              Export CSV
            </button>
            <button
              onClick={loadBookings}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-all flex items-center gap-2"
            >
              <RefreshCw size={18} />
              {t('consulting.refresh') || 'Refresh'}
            </button>
          </div>
        </div>

        {/* Analytics Section */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 className="text-indigo-400" size={24} />
            <h2 className="text-2xl font-bold text-white">Analytics & Metrics</h2>
          </div>

          {/* By Coaching Type */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <TrendingUp size={18} className="text-indigo-400" />
              By Coaching Type
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(analytics.byType).map(([type, data]) => (
                <div key={type} className="bg-slate-800/50 rounded-xl p-4">
                  <div className="text-sm text-slate-400 mb-2">{type.toUpperCase()}</div>
                  <div className="text-2xl font-bold text-white mb-1">{data.count}</div>
                  <div className="text-xs text-slate-400">Bookings</div>
                  <div className="mt-3 flex items-center gap-2">
                    <DollarSign size={16} className="text-emerald-400" />
                    <div>
                      <div className="text-lg font-bold text-emerald-400">{data.revenue} €</div>
                      <div className="text-xs text-slate-400">Avg: {Math.round(data.avgPrice)} €</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* By Month */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">By Month</h3>
            <div className="space-y-3">
              {Object.entries(analytics.byMonth)
                .sort(([a], [b]) => b.localeCompare(a))
                .slice(0, 6)
                .map(([month, data]) => {
                  const monthName = new Date(month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  return (
                    <div key={month} className="bg-slate-800/50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-white font-semibold">{monthName}</div>
                          <div className="text-sm text-slate-400">{data.count} bookings</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-emerald-400">{data.revenue} €</div>
                          <div className="text-xs text-slate-400">Revenue</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-slate-700">
                        {Object.entries(data.byType).map(([type, typeData]) => (
                          <div key={type} className="text-center">
                            <div className="text-xs text-slate-400 mb-1">{type.toUpperCase()}</div>
                            <div className="text-sm font-semibold text-white">{typeData.count}</div>
                            <div className="text-xs text-emerald-400">{typeData.revenue} €</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Recent Days */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Recent Days</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {Object.entries(analytics.byDay)
                .sort(([a], [b]) => b.localeCompare(a))
                .slice(0, 8)
                .map(([day, data]) => (
                  <div key={day} className="bg-slate-800/50 rounded-lg p-3">
                    <div className="text-xs text-slate-400 mb-1">
                      {new Date(day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                    <div className="text-lg font-bold text-white">{data.count}</div>
                    <div className="text-xs text-emerald-400">{data.revenue} €</div>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Bookings Table */}
        {loading ? (
          <div className="text-center text-slate-400 py-12">
            {t('consulting.loading') || 'Loading bookings...'}
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="text-center text-slate-400 py-12">
            {t('consulting.noBookings') || 'No bookings found'}
          </div>
        ) : (
          <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      {t('consulting.bookingId') || 'Booking ID'}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      {t('consulting.client') || 'Client'}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      {t('consulting.service') || 'Service'}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      {t('consulting.dateTime') || 'Date & Time'}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      {t('consulting.payment') || 'Payment'}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      {t('consulting.status') || 'Status'}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-300 uppercase tracking-wider">
                      {t('consulting.actions') || 'Actions'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {filteredBookings.map((booking) => (
                    <tr key={booking.bookingId} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="font-mono text-sm text-indigo-400">#{booking.bookingId}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-white font-medium">{booking.clientInfo.fullName}</div>
                        <div className="text-sm text-slate-400">{booking.clientInfo.email}</div>
                        {booking.clientInfo.telegram && (
                          <div className="text-xs text-slate-500">{booking.clientInfo.telegram}</div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="bg-indigo-500/20 text-indigo-300 px-3 py-1 rounded-lg text-xs font-medium">
                          {booking.consultingType.toUpperCase()}
                        </span>
                        <div className="text-xs text-slate-400 mt-1">{booking.duration} min</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-white font-medium">
                          {new Date(booking.date).toLocaleDateString()}
                        </div>
                        <div className="text-sm text-slate-400">
                          {booking.startTime} - {booking.endTime}
                        </div>
                        {booking.googleMeet?.meetingLink && (
                          <a
                            href={booking.googleMeet.meetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-400 hover:text-indigo-300 mt-1 inline-block"
                          >
                            {t('consulting.joinMeeting') || 'Join Meeting'}
                          </a>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-white font-semibold">{booking.price} €</div>
                        {getPaymentStatusBadge(booking.payment.status)}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(booking.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {booking.status === 'confirmed' && (
                            <button
                              onClick={() => handleCancel(booking.bookingId)}
                              className="px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-xs font-medium transition-all"
                            >
                              {t('consulting.cancel') || 'Cancel'}
                            </button>
                          )}
                          <button
                            onClick={() => handleViewBooking(booking)}
                            className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs font-medium transition-all"
                          >
                            {t('consulting.view') || 'View'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Booking Details Modal */}
      <BookingDetailsModal
        isOpen={showBookingModal}
        onClose={() => {
          setShowBookingModal(false);
          setSelectedBooking(null);
        }}
        booking={selectedBooking}
      />
    </div>
  );
};

export default AdminConsultingDashboard;

